import type { Citation } from "@/lib/citations";

/**
 * The primary sources behind the free first aid needs calculator
 * (/free/first-aid-needs-calculator) and the first aid on a construction
 * site guide. Every line the calculator shows names one of these, read on the
 * accessed date:
 *
 *  - the Health and Safety (First-Aid) Regulations 1981 (latest revised
 *    version on legislation.gov.uk), regs 3, 4 and 5;
 *  - the Prescribed Undertakings Regulations 2015, Schedule para 3, which
 *    makes work on a construction site a prescribed undertaking, so a
 *    self-employed person there is a "relevant self-employed person" under
 *    reg 5;
 *  - HSE L74, First aid at work: Guidance on regulations (third edition 2013,
 *    reissued with amendments 2015, 2018 and 2024), read as the PDF;
 *  - HSE's first aid pages;
 *  - CDM 2015 reg 30 (emergency procedures).
 */
export const FIRST_AID_SOURCES_ACCESSED = "2026-10-31";

export const FIRST_AID_REGS_URL = "https://www.legislation.gov.uk/uksi/1981/917/contents";

/** legislation.gov.uk URL of one regulation of the First-Aid Regulations 1981. */
export function firstAidRegulationUrl(regulation: 3 | 4 | 5): string {
  return `https://www.legislation.gov.uk/uksi/1981/917/regulation/${regulation}`;
}

export const L74_URL = "https://www.hse.gov.uk/pubns/priced/l74.pdf";
export const L74_PAGE_URL = "https://www.hse.gov.uk/pubns/books/l74.htm";
export const HSE_FIRST_AID_EMPLOYERS_URL = "https://www.hse.gov.uk/firstaid/what-employers-need-to-do.htm";
export const HSE_FIRST_AID_FAQS_URL = "https://www.hse.gov.uk/firstaid/faqs.htm";
export const PRESCRIBED_UNDERTAKINGS_URL = "https://www.legislation.gov.uk/uksi/2015/1583/schedule";
export const CDM_REG_30_URL = "https://www.legislation.gov.uk/uksi/2015/51/regulation/30";
export const NI_FIRST_AID_REGS_URL = "https://www.legislation.gov.uk/nisr/1982/429/contents";
export const HSENI_CDM_URL =
  "https://www.hseni.gov.uk/articles/construction-design-and-management-regulations-northern-ireland-2016";

/** Which source a calculator line cites, so the link can never drift from the label. */
export type SourceKey = "reg3" | "reg4" | "reg5" | "prescribed" | "l74" | "hse-employers" | "cdm30";

export const SOURCE_URLS: Readonly<Record<SourceKey, string>> = {
  reg3: firstAidRegulationUrl(3),
  reg4: firstAidRegulationUrl(4),
  reg5: firstAidRegulationUrl(5),
  prescribed: PRESCRIBED_UNDERTAKINGS_URL,
  l74: L74_URL,
  "hse-employers": HSE_FIRST_AID_EMPLOYERS_URL,
  cdm30: CDM_REG_30_URL,
};

const ACCESSED = FIRST_AID_SOURCES_ACCESSED;

export const FIRST_AID_SOURCES: readonly Citation[] = [
  {
    title: "The Health and Safety (First-Aid) Regulations 1981, regulation 3 (duty of employer to make provision for first-aid)",
    url: firstAidRegulationUrl(3),
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Health and Safety (First-Aid) Regulations 1981, regulation 4 (duty of employer to inform employees of the arrangements)",
    url: firstAidRegulationUrl(4),
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Health and Safety (First-Aid) Regulations 1981, regulation 5 (duty of relevant self-employed person to provide first-aid equipment)",
    url: firstAidRegulationUrl(5),
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title:
      "The Health and Safety at Work etc. Act 1974 (General Duties of Self-Employed Persons) (Prescribed Undertakings) Regulations 2015, Schedule paragraph 3 (construction)",
    url: PRESCRIBED_UNDERTAKINGS_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "L74 First aid at work: Guidance on regulations (third edition 2013, reissued with amendments 2015, 2018 and 2024)",
    url: L74_URL,
    publisher: "Health and Safety Executive",
    accessedAt: ACCESSED,
  },
  {
    title: "First aid at work: what employers need to do",
    url: HSE_FIRST_AID_EMPLOYERS_URL,
    publisher: "Health and Safety Executive",
    accessedAt: ACCESSED,
  },
  {
    title: "First aid at work: frequently asked questions",
    url: HSE_FIRST_AID_FAQS_URL,
    publisher: "Health and Safety Executive",
    accessedAt: ACCESSED,
  },
  {
    title: "The Construction (Design and Management) Regulations 2015, regulation 30 (emergency procedures)",
    url: CDM_REG_30_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Health and Safety (First-Aid) Regulations (Northern Ireland) 1982",
    url: NI_FIRST_AID_REGS_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
];
